import { LoadingSmall } from "@/components/LoadingSmall";
import { PermissionChecker2 } from "@/components/permission/PermissionChecker2";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { getChannel, getContent, getContentGroups } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useQuery } from "@tanstack/react-query";
import { EditIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { SelectedMediaPreviewContentDialog } from "./SelectedMediaPreviewContentDialog";
import { AxMediaPreviewContent } from "@/components/ax/mediaGallery/AxMediaPreviewContent";

export const SelectedMediaPreview: React.FC<{
  contentIntId?: number;
  contentGroupIntId?: number;
  channelIntId?: number;
  showInlineContentPreview?: boolean;
}> = ({ contentIntId, contentGroupIntId, channelIntId, showInlineContentPreview = false }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const { data: channelData, isLoading: channelLoading } = useQuery({
    queryFn: () =>
      getChannel(
        {
          numberOfObjectsPerPage: 99999999,
          pageNumber: 1,
        },
        {
          Firm_Id: String(activeFirm?.firmGuid),
        }
      ),
    queryKey: ["getChannel"],
    enabled: !!channelIntId,
  });

  const { data: contentGroupData, isLoading: contentGroupLoading } = useQuery({
    queryKey: ["getContentGroups", activeFirm?.firmGuid],
    queryFn: () =>
      getContentGroups(
        {
          numberOfObjectsPerPage: 99999999,
          pageNumber: 1,
        },
        {
          FirmGuid: activeFirm?.firmGuid,
        }
      ),
    enabled: !!contentGroupIntId,
  });

  const { data: contentData, isLoading: contentLoading } = useQuery({
    queryKey: ["getContent", activeFirm?.firmGuid, contentIntId],
    queryFn: () =>
      getContent(
        {
          numberOfObjectsPerPage: 99999999,
          pageNumber: 1,
        },
        {
          FirmGuid: activeFirm?.firmGuid,
        }
      ),
    enabled: !!contentIntId,
  });

  const { goToContentGroupDetail, goToChannelDetail } = useAppNavigate();

  if (channelLoading || contentGroupLoading || contentLoading) {
    return <LoadingSmall />;
  }

  if (channelIntId) {
    const channel = channelData?.data.result.find((channel) => channel.intId === channelIntId);
    if (!channel) {
      return "-";
    }
    return (
      <div className="flex items-center gap-2 text-sm">
        {channel.name}
        <PermissionChecker2 entity="Channel" rights={["GetDetails"]}>
          <EditIcon className="w-4 h-4 cursor-pointer" onClick={() => goToChannelDetail(channel.id)} />
        </PermissionChecker2>
      </div>
    );
  }

  if (contentGroupIntId) {
    const contentGroup = contentGroupData?.data.result.find((contentGroup) => contentGroup.intId === contentGroupIntId);
    if (!contentGroup) {
      return "-";
    }
    return (
      <div className="flex items-center gap-2 text-sm mt-1">
        <div className="flex items-center gap-1">
          {contentGroup?.name}{" "}
          {contentGroup?.screenSizeName && (
            <span className="text-xs text-primary underline">({contentGroup.screenSizeName})</span>
          )}
        </div>
        <PermissionChecker2 entity="ContentGroup" rights={["GetDetails"]}>
          <EditIcon
            className="w-4 h-4 cursor-pointer"
            onClick={() => goToContentGroupDetail(contentGroup.contentGroupGuid)}
          />
        </PermissionChecker2>
      </div>
    );
  }

  if (contentIntId) {
    const content = contentData?.data.result.find((content) => content.intId === contentIntId);
    if (!content) {
      return "-";
    }

    if (!showInlineContentPreview) {
      return (
        <div className="mt-1">
          <div className="flex items-center gap-1 text-sm">
            {content?.name}{" "}
            {content?.screenSizeName && (
              <span className="text-xs text-primary underline">({content.screenSizeName})</span>
            )}
            <span className="flex-1">
              <SelectedMediaPreviewContentDialog contentDetails={content} />
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-1" style={{ maxWidth: "400px" }}>
        <AxMediaPreviewContent contentGuid={content.contentGuid} showActions />
      </div>
    );
  }

  return <div className="text-muted-foreground text-xs p-2">{t("channel.noMediaSelected")}</div>;
};
